import { Box, Button, CardContent } from '@mui/material'
import Card from '@mui/material/Card'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { useParams } from 'react-router'
import {
  Factsheet_Effectiveness_Classification_Score_Value_Insert_Input,
  Factsheet_Effectiveness_Evaluation,
  Factsheet_Effectiveness_Evaluation_Insert_Input,
  File as GraphqlFile,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { usePrompt } from 'src/routing/routing-hooks'
import {
  CATEGORY_ID_PREFIX,
  ClassificationCategoryAndScores,
  ClassificationScoresForm,
  fetchFactsheetEffectivenessClassificationData,
} from 'src/screens/factsheets/factsheet/FactsheetEffectivenessClassificationUtils'
import {
  deleteFactsheetEffectivenessEvaluationMutation,
  fetchFactsheetEvidenceOfEffectivenessEdit,
  insertFactsheetEffectivenessEvaluationMutation,
  upsertFactsheetEffectivenessClassificationValuesMutation,
} from 'src/screens/factsheets/factsheet/factsheetQueries'
import {
  EvaluationFileItem,
  EvidenceOfEffectivenessFilesGrid,
} from 'src/screens/shared/evidence-of-effectiveness/EvidenceOfEffectivenessFilesGrid'
import { EvidenceOfEffectivenessFileUploadModal } from 'src/screens/shared/evidence-of-effectiveness/EvidenceOfEffectivenessFileUploadModal'
import { mapToEvaluationFileItem } from 'src/screens/shared/evidence-of-effectiveness/utils/EvidenceOfEffectivenessUtils'
import { originType } from 'src/screens/shared/implementation/details/milestone/details/default-milestone/edit/MilestoneDetailsEditForm'
import { FileService } from 'src/service/axios/FileService'
import { usePermissionsForFactsheetDetailsDescriptionPage } from 'src/service/security/PermissionHook'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { ENTITY_TYPE, FILE_TYPE } from 'src/shared/constants/constants'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { maxChar } from 'src/shared/form/validation/validators'
import { UploadIcon } from 'src/shared/icons/Icons'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import { queryFiles } from 'src/shared/presentation/fileQueries'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { Section } from 'src/shared/presentation/Section'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useDelayedSubmit } from 'src/shared/utils/hooks/submit-hook'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import { useClient } from 'urql'

const decorators = createDecorators()

export const FactsheetEvidenceOfEffectivenessEditPage = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const locale = useUserLocale()
  const { factsheetId } = useParams()
  const factsheet_id = parseInt(factsheetId as string)

  const navigate = useDelayedNavigate()
  const submit = useDelayedSubmit()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const originRef = useRef<originType>('SAVE')

  const [loading, setLoading] = useState(false)
  const [isDirtyUploadModal, setIsDirtyUploadModal] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const [isUploadModalSubmitted, setIsUploadModalSubmitted] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  const [factsheetClassificationCategories, setFactsheetClassificationCategories] = useState<
    ClassificationCategoryAndScores[]
  >([])
  const [initialValues, setInitialValues] = useState<ClassificationScoresForm>({ scores: {}, classificationReason: '' })
  const [factsheetEvaluationFileItems, setFactsheetEvaluationFileItems] = useState<EvaluationFileItem[]>([])
  const [factsheetEvaluationFileItemsToBeDeleted, setFactsheetEvaluationFileItemsToBeDeleted] = useState<
    EvaluationFileItem[]
  >([])

  const {
    canEdit,
    canEditClassificationData,
    loading: loadingPermissions,
    metadata: { enableEvaluationFileUpload },
  } = usePermissionsForFactsheetDetailsDescriptionPage(factsheet_id)

  // data fetch
  const initData = useCallback(async () => {
    setLoading(true)

    try {
      // fetch classification data only if the user can edit it
      if (canEditClassificationData) {
        const { factsheetClassificationCategories, values } = await fetchFactsheetEffectivenessClassificationData(
          factsheet_id,
          urqlClient,
          locale,
        )

        setFactsheetClassificationCategories(factsheetClassificationCategories)
        setInitialValues(values)
      }

      // fetch evaluations
      const { data } = await urqlClient
        .query<{
          factsheet_effectiveness_evaluation: Query_Root['factsheet_effectiveness_evaluation']
        }>(fetchFactsheetEvidenceOfEffectivenessEdit, { id: factsheet_id })
        .toPromise()

      if (data?.factsheet_effectiveness_evaluation === undefined || data?.factsheet_effectiveness_evaluation === null) {
        notificationService.operationFailed()
        return
      }

      // fetch files for evaluations
      const { data: dataFiles } = await urqlClient
        .query<
          {
            file: Query_Root['file']
          },
          {
            fileType: string
            entityType: string
            entityIds: number[]
          }
        >(queryFiles, {
          fileType: FILE_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
          entityType: ENTITY_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
          entityIds: data.factsheet_effectiveness_evaluation.map((e) => e.id) ?? [],
        })
        .toPromise()

      if (dataFiles?.file === undefined || dataFiles?.file === null) {
        notificationService.operationFailed()
        return
      }

      const factsheetEvaluation: Array<Factsheet_Effectiveness_Evaluation> = data.factsheet_effectiveness_evaluation
      const factsheetEvaluationFiles: Array<GraphqlFile> = dataFiles.file

      const evaluationFileItems: EvaluationFileItem[] = factsheetEvaluation.map((evaluation) => {
        const file = factsheetEvaluationFiles.find((f) => f.entity_id === evaluation.id)
        return mapToEvaluationFileItem(evaluation, file)
      })

      setFactsheetEvaluationFileItems(evaluationFileItems)
    } catch (error) {
      notificationService.operationFailed()
    } finally {
      setLoading(false)
    }
  }, [canEditClassificationData, factsheet_id, locale, notificationService, urqlClient])

  useEffect(() => {
    // Load data only if file upload is enabled (new factsheets)
    if (enableEvaluationFileUpload === true) {
      initData()
    }
  }, [enableEvaluationFileUpload, initData])

  // handlers
  const handleFileUploadsAndDeletions = useCallback(
    async (
      fileItems: EvaluationFileItem[],
      deletedFileItems: EvaluationFileItem[],
      factsheetId: string,
    ): Promise<boolean> => {
      let hasErrorInsertingFiles = false

      try {
        // Insert or update files and evaluations
        for (const fileItem of fileItems) {
          // Prepare single evaluation object
          const evaluationToInsert: Factsheet_Effectiveness_Evaluation_Insert_Input = {
            ...(fileItem.entityId != null ? { id: fileItem.entityId } : {}), // if entityId exists, include it for update otherwise it's an insert
            factsheet_id: parseInt(factsheetId),
            evaluation_index: fileItem.evaluationIndex,
            name: fileItem.name,
            author: fileItem.author,
            year_publication: fileItem.yearPublication,
            publication_authorized: fileItem.publish,
          }

          // Insert single evaluation
          const { data, error } = await urqlClient
            .mutation<{
              insert_factsheet_effectiveness_evaluation_one: Mutation_Root['insert_factsheet_effectiveness_evaluation_one']
            }>(insertFactsheetEffectivenessEvaluationMutation, {
              object: evaluationToInsert,
            })
            .toPromise()

          if (error || !data?.insert_factsheet_effectiveness_evaluation_one) {
            console.log(error)
            hasErrorInsertingFiles = true
            continue
          }

          // Get the inserted/updated entity ID
          const entityId = data.insert_factsheet_effectiveness_evaluation_one.id

          // Upload file if it doesn't have a UUID (new file) and is valid file
          if ((fileItem.file as any)?.isValid === false) {
            continue
          }

          if (fileItem.fileUUID === null && fileItem.file) {
            try {
              await FileService.upload(
                fileItem.file,
                FILE_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
                ENTITY_TYPE.FACTSHEET_EFFECTIVENESS_EVALUATION,
                entityId,
                fileItem.file.name,
              )
            } catch (error) {
              hasErrorInsertingFiles = true
            }
          }
        }

        // Delete files and evaluations that were removed
        for (const fileItem of deletedFileItems) {
          // Delete file if it has a UUID
          if (fileItem.fileUUID !== null) {
            try {
              await FileService.remove(fileItem.fileUUID)
            } catch (error) {
              hasErrorInsertingFiles = true
            }
          }

          // Delete evaluation
          if (fileItem.entityId !== null) {
            const { data, error } = await urqlClient
              .mutation<{
                delete_factsheet_effectiveness_evaluation_by_pk: Mutation_Root['delete_factsheet_effectiveness_evaluation_by_pk']
              }>(deleteFactsheetEffectivenessEvaluationMutation, {
                id: fileItem.entityId,
              })
              .toPromise()

            if (!data?.delete_factsheet_effectiveness_evaluation_by_pk) {
              console.log('No evaluation deleted')
              continue
            }

            if (error) {
              console.log(error)
              hasErrorInsertingFiles = true
              continue
            }
          }
        }
      } catch (error) {
        console.log(error)
        throw error
      }

      return hasErrorInsertingFiles
    },
    [urqlClient],
  )

  const handleSubmitClassificationData = useCallback(
    async (values: ClassificationScoresForm): Promise<void> => {
      const sanitizedClassificationReason = HtmlSanitizer.sanitize(values.classificationReason)

      const scoreValuesToInsert: Factsheet_Effectiveness_Classification_Score_Value_Insert_Input[] = Object.entries(
        values.scores,
      )
        .filter(([, scoreId]) => scoreId != null)
        .map(([categoryKey, scoreId]) => ({
          factsheet_id,
          category_id: parseInt(categoryKey.replace(CATEGORY_ID_PREFIX, '')),
          score_id: scoreId as number,
        }))
        .filter((scoreValue) => !Number.isNaN(scoreValue.category_id))

      const { error } = await urqlClient
        .mutation<{
          delete_factsheet_effectiveness_classification_score_value: Mutation_Root['delete_factsheet_effectiveness_classification_score_value']
          insert_factsheet_effectiveness_classification_score_value: Mutation_Root['insert_factsheet_effectiveness_classification_score_value']
          update_factsheet: Mutation_Root['update_factsheet']
        }>(upsertFactsheetEffectivenessClassificationValuesMutation, {
          factsheetId: factsheet_id,
          objects: scoreValuesToInsert,
          effectivenessClassificationReason: sanitizedClassificationReason,
        })
        .toPromise()

      if (error) {
        throw error
      }
    },
    [factsheet_id, urqlClient],
  )

  const onBack = useCallback(() => {
    const route = ROUTES.FactsheetDescriptionRoot.params({ factsheetId })
    navigate(`${route}#evidence-of-effectiveness`)
  }, [factsheetId, navigate])

  // form save
  const submitLocal = useCallback(
    async (values: ClassificationScoresForm) => {
      setLoading(true)

      try {
        // submit classification data only if user can edit it
        if (canEditClassificationData) {
          await handleSubmitClassificationData(values)
        }

        let hasErrorInsertingFiles = false

        // Only proceed with uploads and deletions if the modal was submitted
        if (isUploadModalSubmitted) {
          hasErrorInsertingFiles = await handleFileUploadsAndDeletions(
            factsheetEvaluationFileItems,
            factsheetEvaluationFileItemsToBeDeleted,
            factsheetId as string,
          )
        }

        if (hasErrorInsertingFiles) {
          notificationService.info(getMessage('notification.factsheet.edit.evidence_of_effectiveness.partial_failure'))
        } else {
          notificationService.changesSaved()
        }

        setFormDirty(false)

        if (originRef.current === 'SAVE_AND_BACK') {
          onBack()
        }
      } catch (error) {
        notificationService.operationFailed()
      } finally {
        setLoading(false)
        setIsDirtyUploadModal(false)
        setIsUploadModalSubmitted(false)
      }
    },
    [
      canEditClassificationData,
      isUploadModalSubmitted,
      handleSubmitClassificationData,
      handleFileUploadsAndDeletions,
      factsheetEvaluationFileItems,
      factsheetEvaluationFileItemsToBeDeleted,
      factsheetId,
      notificationService,
      getMessage,
      onBack,
    ],
  )

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    const form = document.getElementById('edit-form')

    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
      return
    }

    submitLocal(initialValues)
  }

  // Upload Modal handlers
  const handleOpenEvidenceOfEffectivenessFileUploadModal = useCallback(() => {
    setUploadModalOpen(true)
  }, [])

  const handleCloseEvidenceOfEffectivenessFileUploadModal = useCallback(() => {
    setUploadModalOpen(false)
  }, [])

  const handleSaveEvidenceOfEffectivenessFileUploadModal = useCallback(
    (values: EvaluationFileItem[], deletedItems: EvaluationFileItem[]) => {
      // modal was submitted with modified values inside
      if (isDirtyUploadModal) {
        setIsUploadModalSubmitted(true)
      }
      setFactsheetEvaluationFileItems(values)
      setFactsheetEvaluationFileItemsToBeDeleted(deletedItems)
    },
    [isDirtyUploadModal],
  )

  usePrompt(getMessage('notification.form.unsaved.changes'), isUploadModalSubmitted || isDirtyUploadModal || formDirty)

  // Authorization check
  if (!loadingPermissions && !canEdit) {
    return <NotAuthorized inEditPage />
  }

  // Special case: cannot access new edit page for Evaluation Effectiveness field if file upload is disabled
  if (!loadingPermissions && (enableEvaluationFileUpload === false || enableEvaluationFileUpload === null)) {
    return <NotAuthorized inEditPage />
  }

  return (
    <>
      <ScreenLayout
        title={getMessage('label.factsheet.edit.evidence_of_effectiveness')}
        onBack={onBack}
        hasSecondLevelNavigation={false}
        actions={
          <>
            <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
            <SaveButton origin="header" onClick={onSubmit('SAVE')} />
          </>
        }
      >
        <PageLayout>
          {!loadingPermissions && (
            <>
              {/* Classification section */}
              {canEditClassificationData && (
                <Section
                  id={'evidence-of-effectiveness-score'}
                  title={getMessage('label.factsheet.edit.evidence_of_effectiveness.classification.title')}
                  helpAndInstructions={
                    <HelpAndInstructions labelKey="label.factsheet.edit.evidence_of_effectiveness.help.and.instructions.classification" />
                  }
                >
                  <Form<ClassificationScoresForm>
                    initialValues={initialValues}
                    onSubmit={submit(submitLocal)}
                    decorators={decorators}
                    render={({ handleSubmit }) => {
                      return (
                        <form id="edit-form" onSubmit={handleSubmit} noValidate>
                          {factsheetClassificationCategories.map((category) => {
                            return (
                              <AutoCompleteField
                                key={category.categoryId}
                                name={`scores.${CATEGORY_ID_PREFIX}${category.categoryId}`}
                                label={category.categoryName}
                                options={category.scores}
                              />
                            )
                          })}
                          <HtmlEditorField
                            name="classificationReason"
                            label={getMessage('label.factsheet.edit.evidence_of_effectiveness.classification.reason')}
                            validate={maxChar(2000)}
                          />
                          <FormSpy
                            subscription={{ dirty: true }}
                            onChange={(props: { dirty: boolean }) => {
                              setFormDirty(props.dirty)
                            }}
                          />
                        </form>
                      )
                    }}
                  />
                </Section>
              )}
              {/* Uploaded File Section */}
              <Section
                id={'evidence-of-effectiveness-file-upload'}
                title={getMessage('label.factsheet.edit.evidence_of_effectiveness.file_upload.section.title')}
                actionButton={
                  <Button
                    variant="text"
                    startIcon={<UploadIcon />}
                    onClick={handleOpenEvidenceOfEffectivenessFileUploadModal}
                  >
                    {getMessage('button.manage-uploads')}
                  </Button>
                }
                helpAndInstructions={
                  <HelpAndInstructions labelKey="label.help.factsheet.edit.evidence_of_effectiveness.file_upload.section.title" />
                }
              >
                <Card>
                  <CardContent>
                    <Box>
                      <EvidenceOfEffectivenessFilesGrid
                        editMode={false}
                        showPublishCheckbox={true}
                        loading={loading}
                        rows={factsheetEvaluationFileItems}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Section>
            </>
          )}
        </PageLayout>
      </ScreenLayout>

      <EvidenceOfEffectivenessFileUploadModal
        helpAndInstructionsKey={'label.help.factsheet.modal-dialog.evidence_of_effectiveness.file_upload.section.title'}
        initialValues={factsheetEvaluationFileItems}
        initialDeletedItems={factsheetEvaluationFileItemsToBeDeleted}
        open={uploadModalOpen}
        setDirtyUploadModal={setIsDirtyUploadModal}
        onCancel={handleCloseEvidenceOfEffectivenessFileUploadModal}
        onSave={handleSaveEvidenceOfEffectivenessFileUploadModal}
      />
    </>
  )
}
